import { quicksand } from '@/utilities/fontImport'
import { cn } from '@/utilities/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  `inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${quicksand.className}`,
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        clear: '',
        default: 'h-10 px-4 py-2',
        icon: 'h-10 w-10',
        lg: 'h-11 rounded px-8',
        sm: 'h-9 rounded px-3',
      },
      variant: {
        default: cn(
          'bg-secondary hover:bg-light-red rounded-full p-5 text-white active:scale-95 shadow-md',
        ),
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        ghost: 'bg-white text-black rounded-3xl p-6',
        link: 'text-primary items-start justify-start underline-offset-4 hover:underline',
        outline: 'text-primary px-0',
        secondary: 'bg-transparent text-lg',
        basicWhite: cn('text-white p-0'),
        inputStyle: '',
        gradientOrangeToPink: cn(
          'relative isolate overflow-hidden rounded-full p-5 text-white shadow-md active:scale-95',
          'transition-transform duration-300',
          'bg-bg-orange-to-purple',
          "before:absolute before:inset-0 before:-z-10 before:content-['']",
          'before:bg-bg-purple-to-orange before:opacity-0 before:transition-opacity before:duration-400',
          'hover:before:opacity-100',
        ),
        gradientPrimaryToPink: cn(
          'relative isolate overflow-hidden rounded-full p-5 text-white shadow-md active:scale-95',
          'transition-transform duration-300',
          'bg-bg-purple-to-light-pink',
          "before:absolute before:inset-0 before:-z-10 before:content-['']",
          'before:bg-bg-light-pink-to-purple before:opacity-0 before:transition-opacity before:duration-400',
          'hover:before:opacity-100',
        ),
        purple: cn('bg-primary rounded-full p-5 text-white active:scale-95 shadow-md'),
        pillOutline: cn(
          'bg-white border border-light-pink rounded-full p-5 text-primary active:scale-95',
        ),
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  className,
  size,
  variant,
  ref,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
}

export { Button, buttonVariants }
